import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SEOHead from '@/components/feature/SEOHead';
import SEOSchema from '@/components/feature/SEOSchema';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [statusFilter, setStatusFilter] = useState<'all' | '连载中' | '已完结'>('all');

  const currentSlug = slug || 'all';
  const category = categories.find((c) => c.slug === currentSlug);
  const categoryName = category?.name || '全部';

  const filteredComics = useMemo(() => {
    let result = currentSlug === 'all'
      ? [...comics]
      : comics.filter((c) => c.category === currentSlug);

    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }

    return result;
  }, [currentSlug, sortBy, statusFilter]);

  const title = `${categoryName}韩漫 - 无删减韩漫在线观看 - 韩漫大全`;
  const description = `${categoryName}韩漫专题，免费韩国漫画大全，无删减韩漫在线观看。${category?.description || '涵盖各种题材的韩国漫画资源'}`;

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        keywords={`${categoryName}韩漫,韩漫网站,无删减韩漫在线观看,漫画韩漫网址,免费韩国漫画大全`}
        canonicalUrl={`https://www.manhuakr.com/category/${currentSlug}`}
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: `${categoryName} - 韩漫大全`,
          description,
          url: `https://www.manhuakr.com/category/${currentSlug}`,
          inLanguage: 'zh-CN',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={getCategoryBreadcrumb(currentSlug)} />

            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50">
                {categoryName}韩漫
              </h1>
              <p className="text-foreground-500 text-sm mt-2">
                {category?.description || '浏览全部韩国漫画作品'}
              </p>
            </div>

            {/* Sub-category tabs */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap cursor-pointer ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 font-semibold'
                      : 'bg-background-100 text-foreground-400 hover:text-foreground-50 hover:bg-background-200/70 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <span className="text-foreground-500 text-xs">状态：</span>
                {(['all', '连载中', '已完结'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-3 py-1.5 rounded-md text-xs transition-all whitespace-nowrap cursor-pointer ${
                      statusFilter === s
                        ? 'bg-secondary-500/20 text-secondary-400 border border-secondary-500/30'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-200 border border-background-200/70'
                    }`}
                  >
                    {s === 'all' ? '全部' : s}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-foreground-500 text-xs">排序：</span>
                {([
                  { value: 'latest', label: '最新' },
                  { value: 'popular', label: '热门' },
                  { value: 'rating', label: '评分' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-md text-xs transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-secondary-500/20 text-secondary-400 border border-secondary-500/30'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-200 border border-background-200/70'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Comic grid */}
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-product-shop>
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-foreground-500 text-2xl"></i>
                </div>
                <p className="text-foreground-400 text-sm">暂无符合条件的漫画</p>
              </div>
            )}

            {/* Pagination placeholder */}
            <div className="flex items-center justify-center gap-2 mt-10">
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 text-sm cursor-pointer">
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm font-medium cursor-pointer">
                1
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:text-foreground-50 text-sm transition-colors cursor-pointer">
                2
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:text-foreground-50 text-sm transition-colors cursor-pointer">
                3
              </button>
              <span className="text-foreground-500 text-sm">...</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 text-sm cursor-pointer">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}